import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Profile = Database['public']['Tables']['profiles']['Row'];

export const useProfile = (userId?: string) => {
  const queryClient = useQueryClient();

  // Получение профиля
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['profile', userId],
    queryFn: async (): Promise<Profile | null> => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Обновление профиля
  const updateProfile = useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!userId) throw new Error('User ID is required');

      // Разрешаем обновлять ТОЛЬКО user_settings через RPC
      if (!updates.user_settings) {
        throw new Error('updateProfile supports only user_settings updates. Use RPC functions for other fields.');
      }

      const { data, error } = await supabase
        .rpc('update_user_settings', {
          new_settings: updates.user_settings
        });

      if (error) throw error;

      // Возвращаем обновленный профиль с новыми user_settings
      return { ...profile, user_settings: data } as Profile;
    },
    onSuccess: () => {
      // Инвалидируем кеш после успешного обновления
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
    },
  });

  // Обновление языка профиля
  const updateLang = useMutation({
    mutationFn: async (newLang: string) => {
      const { data, error } = await supabase.rpc('update_profile_lang', { new_lang: newLang });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
    },
  });

  // Обновление валюты профиля
  const updateCurrency = useMutation({
    mutationFn: async (newCurrency: string) => {
      const { data, error } = await supabase.rpc('update_profile_currency', { new_currency: newCurrency });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
    },
  });

  // Получение баланса
  const { data: balance } = useQuery({
    queryKey: ['profile', userId, 'balance'],
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;

      const { data, error } = await supabase
        .from('profiles')
        .select('balance')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data?.balance ?? 0;
    },
    enabled: !!userId,
  });

  return {
    profile,
    balance,
    isLoading,
    error,
    updateProfile: updateProfile.mutate,
    isUpdating: updateProfile.isPending,
    updateError: updateProfile.error,
    updateLang: updateLang.mutate,
    updateCurrency: updateCurrency.mutate,
  };
};

